import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import { comentariosAvances } from "src/app/shared/model/gestion-proyectos/comentariosAvances";
import { Etapa } from "src/app/shared/model/gestion-proyectos/etapa/etapa";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { SeveridadRiesgo } from "src/app/shared/model/gestion-proyectos/riesgos/severidad-riesgo.model";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { ConsultaRiesgosComponent } from "../gestion_riesgos/consulta-riesgos/consulta-riesgos.component";
import { ProyectosService } from "../proyectos.service";
import {
  ProyectoConIndicadores,
  decorarProyectoParaVista,
  seguimientosRecientesPrimero,
} from "../utilerias/proyecto-indicadores.util";
import { abrirSeguimientoDeProyecto } from "../utilerias/seguimiento-proyecto";
import { CODIGOS_VERSIONES, EtapaVersiones } from "./etapas-versiones.model";
import { EtapasVersionesService } from "./etapas-versiones.service";
import { EtapasVersionesComponent } from "./etapas-versiones/etapas-versiones.component";
import {
  PestanaProyecto,
  etapaDelProyecto,
  idEtapaDeRuta,
  llaveCopiaProyecto,
  pestanaDeTexto,
  rutaAltaRiesgo,
  rutaEditorEtapa,
  rutaEditorProyecto,
  rutaListaProyectos,
} from "./proyecto-pagina.rutas";
import { ProyectoResumenComponent } from "./proyecto-resumen/proyecto-resumen.component";

/** Una pestaña de la página: su clave en ?pestana=, su texto y su ícono. */
interface PestanaPagina {
  clave: PestanaProyecto;
  texto: string;
  icono: string;
}

const PESTANAS: readonly PestanaPagina[] = [
  { clave: "resumen", texto: "Resumen", icono: "dashboard" },
  { clave: "etapas", texto: "Etapas y versiones", icono: "layers" },
  { clave: "seguimientos", texto: "Seguimientos", icono: "track_changes" },
  { clave: "riesgos", texto: "Riesgos", icono: "warning_amber" },
];

const SIN_PROYECTO = "No se pudo consultar el proyecto. Revisa tu conexión e intenta de nuevo.";

/**
 * Gestión de Proyectos · página del proyecto (plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8, maqueta
 * Main.dc.html): VISUALIZARPROY/proyecto/:id en los cuatro prefijos, con
 * ?pestana=resumen|etapas|seguimientos|riesgos y ?etapa=<id>.
 *
 * Carga el proyecto por PROYECTOS_PAGINA (no por la copia de la lista), así
 * que funciona con una liga compartida o en otra pestaña del navegador. El
 * encabezado y la pestaña Resumen pintan lo mismo que la tarjeta
 * (proyecto-indicadores.util); «Etapas y versiones» es A1. Los editores de
 * proyecto y de etapa y el alta de riesgo reciben la copia del proyecto como
 * desde la tarjeta, más state.returnUrl para volver aquí.
 *
 * Resumen, Seguimientos y Riesgos no tienen maqueta elegida (D19): reúnen lo
 * que ya existe (indicadores de la tarjeta, historial de avances con
 * «Agregar seguimiento», y la consulta de riesgos del proyecto).
 */
@Component({
  selector: "app-proyecto-pagina",
  standalone: true,
  templateUrl: "./proyecto-pagina.component.html",
  styleUrls: ["./proyecto-pagina.component.css"],
  imports: [CommonModule, MatButtonModule, RouterLink, ConsultaRiesgosComponent, EtapasVersionesComponent, ProyectoResumenComponent],
})
export class ProyectoPaginaComponent implements OnInit, OnDestroy {
  readonly pestanas = PESTANAS;

  idProyecto = "";
  pestana: PestanaProyecto = "resumen";
  /** ?etapa=: la etapa elegida en «Etapas y versiones». */
  claveEtapa: string | null = null;
  proyecto: ProyectoConIndicadores | null = null;
  seguimientos: comentariosAvances[] = [];
  severidad: SeveridadRiesgo[] = [];
  /** Defectos con tenant (PROYECTOS_ETAPAS_RESUMEN); null mientras llegan o si no se pudieron leer. */
  totalDefectos: number | null = null;
  rutaLista = "";
  cargando = false;
  error = "";

  private readonly subs: Subscription[] = [];
  private lectura?: Subscription;
  private lecturaDefectos?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly servicio: EtapasVersionesService,
    private readonly proyectosService: ProyectosService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.rutaLista = rutaListaProyectos(this.router.url);
    this.subs.push(
      this.route.paramMap.subscribe((parametros) => {
        const id = (parametros.get("id") ?? "").trim();
        if (id !== this.idProyecto) {
          this.idProyecto = id;
          this.cargar();
        }
      }),
      this.route.queryParamMap.subscribe((parametros) => {
        this.pestana = pestanaDeTexto(parametros.get("pestana"));
        this.claveEtapa = parametros.get("etapa");
      }),
      this.proyectosService.consultaGeneral(UrlConstantes.consultaSeveridad).subscribe({
        next: (respuesta) => {
          this.severidad = respuesta?.list ?? [];
        },
        error: () => {
          this.severidad = [];
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
    this.lectura?.unsubscribe();
    this.lecturaDefectos?.unsubscribe();
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  /** Las etapas del proyecto sin las entradas nulas del arreglo, como las cuentan A1 y la tarjeta. */
  get totalEtapas(): number {
    return this.proyecto?.etapas?.filter(Boolean).length ?? 0;
  }

  trackPestana(_: number, pestana: PestanaPagina): string {
    return pestana.clave;
  }

  trackSeguimiento(indice: number, seguimiento: comentariosAvances): number {
    return seguimiento?.id ?? indice;
  }

  /* ============================ Acciones ============================ */

  /** Lee el proyecto y, con él, sus defectos; un rechazo del servidor (pCodRetorno) se dice tal cual. */
  cargar(): void {
    this.lectura?.unsubscribe();
    if (!this.idProyecto) {
      this.proyecto = null;
      this.error = "Falta el proyecto en la liga.";
      return;
    }
    this.cargando = true;
    this.error = "";
    this.lectura = this.servicio.pagina(this.idProyecto).subscribe({
      next: (respuesta) => {
        this.cargando = false;
        if (respuesta?.pCodRetorno === CODIGOS_VERSIONES.EXITO) {
          this.mostrar(respuesta);
        } else {
          this.proyecto = null;
          this.error = respuesta?.pMensaje?.trim() || "No se encontró el proyecto.";
        }
      },
      error: () => {
        this.cargando = false;
        this.proyecto = null;
        this.error = SIN_PROYECTO;
      },
    });
  }

  /** La etapa elegida en A1 va a ?etapa=, para que la liga la conserve. */
  elegirEtapa(clave: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pestana: "etapas", etapa: clave },
      queryParamsHandling: "merge",
    });
  }

  /** «Editar proyecto»: el editor de siempre, que al terminar regresa aquí. */
  editarProyecto(): void {
    const copia = this.copiaParaEditores();
    if (!copia) {
      return;
    }
    this.router.navigateByUrl(rutaEditorProyecto(this.router.url, this.idProyecto), {
      state: { proyecto: copia, returnUrl: this.router.url },
    });
  }

  /** «Crear etapa» (sin etapa) o «Editar etapa» desde A1; el editor regresa a esta pestaña. */
  editarEtapa(etapaA1: EtapaVersiones | null): void {
    const copia = this.copiaParaEditores();
    if (!copia) {
      return;
    }
    let etapa: Etapa | null = null;
    if (etapaA1) {
      etapa = etapaDelProyecto(copia.etapas, etapaA1.idEtapa, etapaA1.nombre);
      if (!etapa) {
        this.avisar("La etapa ya no está en la copia del proyecto. Vuelve a cargar la página.");
        return;
      }
    }
    // Al volver, la misma etapa queda elegida; con una etapa nueva, la pestaña sin elegir ninguna.
    const etapaAlVolver = etapaA1 ? etapaA1.idEtapa ?? this.claveEtapa : null;
    this.router.navigateByUrl(rutaEditorEtapa(this.router.url, this.idProyecto, idEtapaDeRuta(etapa)), {
      state: { proyecto: copia, etapa, returnUrl: this.urlDePestana("etapas", etapaAlVolver) },
    });
  }

  /** «Agregar seguimiento»: el mismo diálogo de la tarjeta; si se guardó algo, vuelve a leer el proyecto. */
  agregarSeguimiento(): void {
    if (!this.proyecto || this.dialog.openDialogs.length > 0) {
      return;
    }
    abrirSeguimientoDeProyecto(this.dialog, this.proyecto)
      .afterClosed()
      .subscribe((huboCambios) => {
        if (huboCambios) {
          this.cargar();
        }
      });
  }

  /** «Agregar riesgo»: el alta de riesgo de siempre, que regresa a la pestaña Riesgos. */
  agregarRiesgo(): void {
    const proyecto = this.proyecto;
    if (!proyecto) {
      return;
    }
    this.router.navigate(rutaAltaRiesgo(this.router.url, this.idProyecto), {
      state: {
        returnUrl: this.urlDePestana("riesgos", null),
        projectName: proyecto.nombre,
        projectClient: proyecto.nombreCliente,
        project: proyecto,
      },
    });
  }

  /** «Regresar» de la consulta de riesgos: a la lista, como desde la tarjeta. */
  irALista(): void {
    this.router.navigateByUrl(this.rutaLista);
  }

  /* ============================ Internas ============================ */

  private mostrar(respuesta: Proyecto): void {
    this.proyecto = decorarProyectoParaVista(respuesta);
    this.seguimientos = seguimientosRecientesPrimero(this.proyecto.comentariosAvances);
    this.leerDefectos();
  }

  /** «Defectos: N» con tenant, del mismo resumen que usan las tarjetas. */
  private leerDefectos(): void {
    this.lecturaDefectos?.unsubscribe();
    this.totalDefectos = null;
    const id = this.idProyecto;
    this.lecturaDefectos = this.servicio.resumenEtapas([id]).subscribe({
      next: (respuesta) => {
        const resumen = respuesta?.pCodRetorno === CODIGOS_VERSIONES.EXITO ? respuesta.proyectos?.[id] : null;
        this.totalDefectos = resumen ? resumen.totalDefectos : null;
      },
      error: () => {
        this.totalDefectos = null;
      },
    });
  }

  /**
   * La copia del proyecto que reciben los editores, como desde la tarjeta: en
   * state y en sessionStorage (para que el editor aguante una recarga).
   */
  private copiaParaEditores(): Proyecto | null {
    if (!this.proyecto) {
      return null;
    }
    const texto = JSON.stringify(this.proyecto);
    guardarEnSesion(llaveCopiaProyecto(this.router.url, this.idProyecto), texto);
    return JSON.parse(texto) as Proyecto;
  }

  /** Esta página en una pestaña (y una etapa), para la URL de regreso de los editores. */
  private urlDePestana(pestana: PestanaProyecto, etapa: string | null): string {
    const queryParams = etapa ? { pestana, etapa } : { pestana };
    return this.router.serializeUrl(this.router.createUrlTree([], { relativeTo: this.route, queryParams }));
  }

  private avisar(texto: string): void {
    this.snackBar.open(texto, "Cerrar", { duration: 6000, horizontalPosition: "right", verticalPosition: "top" });
  }
}

/**
 * Guarda en sessionStorage; sin él (modo privado, lleno o bloqueado) da
 * falso y el editor sigue con la copia que lleva en state.
 */
function guardarEnSesion(llave: string, valor: string): boolean {
  try {
    sessionStorage.setItem(llave, valor);
    return true;
  } catch {
    return false;
  }
}
